import React, { useMemo, useState } from 'react';
import type { ScheduleSessionItem, SessionStatus } from './ScheduleSessionsPanel';

interface ScheduleSessionsCalendarProps {
  sessions: ScheduleSessionItem[];
}

const pad2 = (value: number): string => `${value}`.padStart(2, '0');

const toDateKey = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const toDisplayDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString();
};

const toDisplayTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const statusColors: Record<SessionStatus, { background: string; color: string; border: string }> = {
  Scheduled: { background: '#f8fafc', color: '#334155', border: '#cbd5e1' },
  InProgress: { background: '#fff7ed', color: '#9a5b00', border: '#fdba74' },
  Completed: { background: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  Cancelled: { background: '#fff1f2', color: '#b91c1c', border: '#fecaca' },
  Rescheduled: { background: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  NoShow: { background: '#f5f3ff', color: '#6d28d9', border: '#c4b5fd' },
};

const weekdayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ScheduleSessionsCalendar: React.FC<ScheduleSessionsCalendarProps> = ({ sessions }) => {
  const initialMonth = useMemo(() => {
    if (sessions.length === 0) {
      const now = new Date();
      return new Date(now.getFullYear(), now.getMonth(), 1);
    }

    const first = new Date(sessions[0].startDateTime);
    return Number.isNaN(first.getTime()) ? new Date() : new Date(first.getFullYear(), first.getMonth(), 1);
  }, [sessions]);

  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);
  const [selectedDateKey, setSelectedDateKey] = useState<string>('');

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, ScheduleSessionItem[]>();
    for (const session of sessions) {
      const key = toDateKey(session.startDateTime);
      if (!key) {
        continue;
      }

      const existing = map.get(key) || [];
      existing.push(session);
      map.set(key, existing);
    }

    Array.from(map.values()).forEach((list) => {
      list.sort((a, b) => new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime());
    });

    return map;
  }, [sessions]);

  const monthLabel = viewMonth.toLocaleString(undefined, { month: 'long', year: 'numeric' });

  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const startOffset = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const gridDays = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const dateCells = Array.from({ length: gridDays }, (_, index) => {
    const dayNumber = index - startOffset + 1;
    const inMonth = dayNumber >= 1 && dayNumber <= daysInMonth;
    const cellDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), inMonth ? dayNumber : 1);
    const key = inMonth
      ? `${viewMonth.getFullYear()}-${pad2(viewMonth.getMonth() + 1)}-${pad2(dayNumber)}`
      : '';

    return {
      inMonth,
      dayNumber,
      key,
      cellDate,
      sessions: inMonth ? sessionsByDate.get(key) || [] : [],
    };
  });

  const selectedSessions = selectedDateKey ? sessionsByDate.get(selectedDateKey) || [] : [];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 14, display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#173159' }}>Session Calendar</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="amd-btn amd-btn-ghost"
              onClick={() => setViewMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() - 1, 1))}
            >
              Prev
            </button>
            <div style={{ minWidth: 160, textAlign: 'center', fontWeight: 700, color: '#1e293b' }}>{monthLabel}</div>
            <button
              type="button"
              className="amd-btn amd-btn-ghost"
              onClick={() => setViewMonth((previous) => new Date(previous.getFullYear(), previous.getMonth() + 1, 1))}
            >
              Next
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 6 }}>
          {weekdayHeaders.map((day) => (
            <div key={day} style={{ textAlign: 'center', color: '#64748b', fontSize: 12, fontWeight: 600 }}>{day}</div>
          ))}

          {dateCells.map((cell, index) => {
            if (!cell.inMonth) {
              return <div key={`blank-${index}`} style={{ minHeight: 96, borderRadius: 8, background: '#f8fafc' }} />;
            }

            const isSelected = selectedDateKey === cell.key;

            return (
              <button
                key={cell.key}
                type="button"
                onClick={() => setSelectedDateKey(cell.key)}
                style={{
                  minHeight: 96,
                  borderRadius: 8,
                  border: `1px solid ${isSelected ? '#2563eb' : '#e2e8f0'}`,
                  background: isSelected ? '#eff6ff' : '#fff',
                  padding: 6,
                  textAlign: 'left',
                  display: 'grid',
                  alignContent: 'start',
                  gap: 4,
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{cell.dayNumber}</div>
                {cell.sessions.slice(0, 3).map((session) => {
                  const colors = statusColors[session.status];
                  return (
                    <div
                      key={session.id}
                      style={{
                        borderRadius: 6,
                        border: `1px solid ${colors.border}`,
                        background: colors.background,
                        color: colors.color,
                        fontSize: 11,
                        padding: '2px 4px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {toDisplayTime(session.startDateTime)} {session.teacherDisplayName || `T${session.teacherUserId}`}
                    </div>
                  );
                })}
                {cell.sessions.length > 3 && (
                  <div style={{ fontSize: 11, color: '#64748b' }}>+{cell.sessions.length - 3} more</div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDateKey && (
        <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, background: '#fff', padding: 14, display: 'grid', gap: 8 }}>
          <h4 style={{ margin: 0, color: '#173159' }}>Sessions on {toDisplayDate(selectedDateKey)}</h4>
          {selectedSessions.length === 0 ? (
            <div style={{ color: '#64748b', fontSize: 13 }}>No sessions on this date.</div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {selectedSessions.map((session) => {
                const colors = statusColors[session.status];
                return (
                  <div key={session.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 8, display: 'grid', gap: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      <div style={{ color: '#0f172a', fontWeight: 600 }}>{toDisplayTime(session.startDateTime)} - {toDisplayTime(session.endDateTime)}</div>
                      <span style={{ borderRadius: 999, border: `1px solid ${colors.border}`, background: colors.background, color: colors.color, fontSize: 11, padding: '2px 8px', fontWeight: 700 }}>
                        {session.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#334155' }}>
                      {session.classroomName || session.studentDisplayName || 'Session'} | {session.teacherDisplayName || `User ${session.teacherUserId}`}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{session.location || session.meetingUrl || 'No location details'}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleSessionsCalendar;
